import { createContext, useContext, useEffect, useState, type PropsWithChildren } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { IMPERSONATION_QUERY_PARAM } from "@/shared/constants/impersonation";
import {
  bootstrapImpersonation,
  clearImpersonationContext,
  getImpersonationContext,
  type ImpersonationContext,
} from "@/shared/utils/impersonation";

interface ImpersonationContextValue {
  impersonation: ImpersonationContext | null;
}

const ImpersonationReactContext = createContext<ImpersonationContextValue | undefined>(undefined);

export function ImpersonationProvider({ children }: PropsWithChildren) {
  const { authUser, isLoading } = useAppContext();
  const [impersonation, setImpersonation] = useState<ImpersonationContext | null>(() =>
    getImpersonationContext(),
  );

  // Re-bootstrap when a new impersonation token arrives, even for the same user.
  // Keying the effect on email alone missed re-impersonating the same person
  // (the banner never reappeared until a different user was chosen).
  const impersonationToken = new URLSearchParams(window.location.search).get(
    IMPERSONATION_QUERY_PARAM,
  );

  useEffect(() => {
    const email = authUser?.email;
    if (!email) {
      if (!isLoading) {
        clearImpersonationContext();
        setImpersonation(null);
      }
      return;
    }

    let cancelled = false;

    const existing = getImpersonationContext();
    if (existing && existing.user_email.toLowerCase() !== email.toLowerCase()) {
      clearImpersonationContext();
      setImpersonation(null);
    }

    bootstrapImpersonation(email)
      .then((context) => {
        if (!cancelled) {
          setImpersonation(context);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setImpersonation(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authUser?.email, isLoading, impersonationToken]);

  return (
    <ImpersonationReactContext.Provider value={{ impersonation }}>
      {children}
    </ImpersonationReactContext.Provider>
  );
}

export function useImpersonation(): ImpersonationContextValue {
  const context = useContext(ImpersonationReactContext);
  if (!context) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }
  return context;
}
